import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Play, 
  ShieldAlert, 
  ArrowUpRight, 
  ArrowDownRight,
  Info,
  Clock,
  Layers,
  UserCheck
} from 'lucide-react';
import { 
  ManipulatedVariable, 
  StepTest, 
  StepDirection 
} from '../types';
import { checkCorrelationConflicts, getGeneralCorrelationNotice } from '../services/correlationEngine';

interface StepTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  mvs: ManipulatedVariable[];
  initialMv?: ManipulatedVariable | null;
  onSaveStep: (step: StepTest) => void;
  currentProjectId?: string;
  currentCampaignId?: string;
  defaultHoldDuration?: number;
}

const getLocalIso = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

export const StepTestModal: React.FC<StepTestModalProps> = React.memo(({
  isOpen,
  onClose,
  mvs,
  initialMv,
  onSaveStep,
  currentProjectId,
  currentCampaignId,
  defaultHoldDuration = 60,
}) => {
  const activeProjectId = currentProjectId || currentCampaignId || 'kfs-apc-2026';
  const [selectedMvTag, setSelectedMvTag] = useState<string>('');
  const [dateStr, setDateStr] = useState<string>(getLocalIso);
  const [engineer, setEngineer] = useState<string>('J. Dhaliwal');
  const [operator, setOperator] = useState<string>('Console Operator');
  const [direction, setDirection] = useState<StepDirection>('+');
  const [stepSize, setStepSize] = useState<number>(5.0);
  const [baselineSp, setBaselineSp] = useState<number>(100.0);
  const [targetSp, setTargetSp] = useState<number>(105.0);
  const [holdDuration, setHoldDuration] = useState<number>(defaultHoldDuration);
  const [comments, setComments] = useState<string>('');
  const [initialDateStr, setInitialDateStr] = useState<string>(getLocalIso);
  const dateInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      const localIso = getLocalIso();
      setDateStr(localIso);
      setInitialDateStr(localIso);
      if (dateInputRef.current) {
        dateInputRef.current.value = localIso;
      }

      const target = initialMv || mvs[0];
      if (target) {
        setSelectedMvTag(target.tag);
        setStepSize(target.approvedStepSize || 5.0);
      }
      setDirection('+');
      setHoldDuration(defaultHoldDuration);
      setComments('');
    }
  }, [isOpen, initialMv?.id, defaultHoldDuration]);

  useEffect(() => {
    const found = mvs.find((m) => m.tag === selectedMvTag);
    if (found) {
      setStepSize(found.approvedStepSize);
    }
  }, [selectedMvTag, mvs]);

  useEffect(() => {
    const delta = direction === '+' ? stepSize : -stepSize;
    const computed = Math.round((baselineSp + delta) * 100) / 100;
    setTargetSp(computed);
  }, [baselineSp, stepSize, direction]);

  const selectedMv = mvs.find((m) => m.tag === selectedMvTag);
  const conflicts = selectedMvTag ? checkCorrelationConflicts(selectedMvTag, mvs) : [];
  const generalNotices = selectedMvTag ? getGeneralCorrelationNotice(selectedMvTag) : [];

  const handleStartStep = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMv) return;

    const chosenDateStr = dateInputRef.current?.value || dateStr || getLocalIso();
    const submissionTime = new Date();
    let finalTimestamp = chosenDateStr.replace('T', ' ');
    let finalCreatedAt = submissionTime.toISOString();

    // If the operator did not manually edit the datetime input, use exact submission time
    if (chosenDateStr === initialDateStr) {
      const freshLocalIso = new Date(submissionTime.getTime() - submissionTime.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      finalTimestamp = freshLocalIso.replace('T', ' ');
      finalCreatedAt = submissionTime.toISOString();
    } else {
      // User customized the date/time, align createdAt to their chosen timestamp
      const customDate = new Date(chosenDateStr);
      if (!isNaN(customDate.getTime())) {
        finalCreatedAt = customDate.toISOString();
      }
    }

    const newStep: StepTest = {
      id: `step-${Date.now()}`,
      campaignId: activeProjectId,
      mvId: selectedMv.id,
      mvTag: selectedMv.tag,
      timestamp: finalTimestamp,
      engineer,
      operator,
      direction,
      stepSize,
      baselineSp,
      targetSp,
      holdDuration,
      status: 'IN_PROGRESS',
      comments,
      createdAt: finalCreatedAt,
    };

    onSaveStep(newStep);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/40 backdrop-blur-xs overflow-y-auto">
      <div 
        className="bg-white border border-slate-200 rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden my-4 sm:my-8 animate-in fade-in-50 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
            <div>
              <h2 className="font-semibold text-slate-900 text-base">Start Step Test</h2>
              <p className="text-[11px] text-slate-500">Initiate setpoint change and begin settling hold duration</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleStartStep} className="p-4 sm:p-6 space-y-4 max-h-[82vh] overflow-y-auto">
          {/* Active Correlation Conflicts Warning Banner */}
          {conflicts.length > 0 && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs space-y-1">
              <div className="flex items-center gap-2 font-semibold text-rose-900">
                <ShieldAlert className="w-4 h-4 text-rose-600 shrink-0" />
                <span>Correlation Notice: Coupled Testing Detected</span>
              </div>
              {conflicts.map((c, i) => (
                <div key={i} className="pl-6 text-[11px] text-rose-700">
                  • {c.message}
                </div>
              ))}
            </div>
          )}

          {conflicts.length === 0 && generalNotices.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-xs flex items-start gap-2">
              <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <span className="font-medium text-amber-900">Interaction Advisory: </span>
                <span className="text-amber-800">{generalNotices[0]}</span>
              </div>
            </div>
          )}

          {/* Row 1: Target MV & Direction */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 text-slate-500" />
                <span>Manipulated Variable (MV)</span>
              </label>
              <select
                value={selectedMvTag}
                onChange={(e) => setSelectedMvTag(e.target.value)}
                className="w-full text-xs font-mono bg-slate-50 text-slate-900 border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
                required
              >
                {mvs.map((m) => (
                  <option key={m.id} value={m.tag}>
                    {m.tag} — {m.description} ({m.controller}) [{m.units}]
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Step Direction</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setDirection('+')}
                  className={`flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold transition-all ${
                    direction === '+'
                      ? 'bg-emerald-600 text-white shadow-xs'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <ArrowUpRight className="w-4 h-4" />
                  <span>+ Up</span>
                </button>
                <button
                  type="button"
                  onClick={() => setDirection('-')}
                  className={`flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold transition-all ${
                    direction === '-'
                      ? 'bg-rose-600 text-white shadow-xs'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <ArrowDownRight className="w-4 h-4" />
                  <span>- Down</span>
                </button>
              </div>
            </div>
          </div>

          {/* Row 2: Setpoint Dynamics */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
            <div className="text-xs font-semibold text-slate-700 flex items-center justify-between">
              <span>Setpoint Step Plan</span>
              <span className="text-[11px] font-mono text-slate-500 font-normal">
                Subcontroller: <strong className="text-slate-800">{selectedMv?.controller}</strong>
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">
                  Baseline SP ({selectedMv?.units || 'units'})
                </label>
                <input
                  type="number"
                  step="any"
                  value={baselineSp}
                  onChange={(e) => setBaselineSp(parseFloat(e.target.value) || 0)}
                  className="w-full bg-white text-slate-900 text-xs font-mono border border-slate-300 rounded-md p-2 text-right focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                  required
                />
              </div>

              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">
                  Step Size (ΔSP)
                </label>
                <input
                  type="number"
                  step="any"
                  value={stepSize}
                  onChange={(e) => setStepSize(parseFloat(e.target.value) || 0)}
                  className="w-full bg-white text-slate-900 text-xs font-mono border border-slate-300 rounded-md p-2 text-right focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                  required
                />
              </div>

              <div className="col-span-2 sm:col-span-1">
                <label className="block text-[11px] font-medium text-slate-600 mb-1">
                  Target SP (Resulting)
                </label>
                <div className="w-full bg-sky-50 text-sky-900 text-xs font-mono font-bold border border-sky-200 rounded-md p-2 text-right flex items-center justify-between">
                  <span className="text-[10px] text-sky-600 font-sans font-medium">New SP:</span>
                  <span>{targetSp} {selectedMv?.units}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Row 3: Hold Duration Selection */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-sky-600" />
                <span>Planned Settling / Hold Duration</span>
              </label>
              <span className="text-[11px] font-mono text-slate-600 font-medium">
                {holdDuration >= 60
                  ? `${holdDuration} min (${+(holdDuration / 60).toFixed(1)}h)`
                  : `${holdDuration} min`}
              </span>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {[15, 30, 45, 60, 90, 120, 180].map((mins) => (
                <button
                  key={mins}
                  type="button"
                  onClick={() => setHoldDuration(mins)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    holdDuration === mins
                      ? 'bg-sky-600 text-white border-sky-600 shadow-xs'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                  }`}
                >
                  {mins}m
                </button>
              ))}
              <div className="flex items-center gap-1 ml-auto">
                <input
                  type="number"
                  step="any"
                  min="1"
                  value={holdDuration || ''}
                  onChange={(e) => setHoldDuration(Math.max(0, parseFloat(e.target.value) || 0))}
                  className="w-20 bg-white text-slate-900 text-xs font-mono border border-slate-300 rounded-lg p-1.5 text-right focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                />
                <span className="text-xs text-slate-500 font-medium">min</span>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 pt-1">
              Once this duration finishes, this variable will blink in the table so you can acknowledge and evaluate steady state.
            </p>
          </div>

          {/* Row 4: Personnel & Timestamp */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-slate-700">Start Date & Time</label>
                <button
                  type="button"
                  onClick={() => {
                    const freshIso = getLocalIso();
                    setDateStr(freshIso);
                    setInitialDateStr(freshIso);
                    if (dateInputRef.current) {
                      dateInputRef.current.value = freshIso;
                    }
                  }}
                  className="text-[10px] text-sky-600 hover:text-sky-800 font-semibold cursor-pointer transition-colors"
                >
                  Reset to Current
                </button>
              </div>
              <input
                ref={dateInputRef}
                type="datetime-local"
                defaultValue={dateStr}
                key={initialDateStr || 'datetime-picker'}
                onClick={(e) => {
                  try {
                    (e.currentTarget as any).showPicker?.();
                  } catch {}
                }}
                className="w-full bg-white text-slate-900 text-xs font-mono border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 cursor-pointer"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1 flex items-center gap-1">
                <UserCheck className="w-3 h-3 text-slate-400" />
                <span>APC Engineer</span>
              </label>
              <input
                type="text"
                value={engineer}
                onChange={(e) => setEngineer(e.target.value)}
                className="w-full bg-white text-slate-900 text-xs border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Console Operator</label>
              <input
                type="text"
                value={operator}
                onChange={(e) => setOperator(e.target.value)}
                className="w-full bg-white text-slate-900 text-xs border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                required
              />
            </div>
          </div>

          {/* Initial Notes */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Initial Notes / Test Objective (Optional)
            </label>
            <textarea
              rows={2}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="e.g. Stepping reflux up to check column top temperature sensitivity."
              className="w-full bg-white text-slate-900 text-xs border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
            />
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <span className="text-[11px] text-slate-500">
              Evaluation checklist will be performed after the step completes.
            </span>
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-xs font-medium text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 text-xs font-semibold rounded-lg shadow-xs bg-emerald-600 hover:bg-emerald-700 text-white transition-colors flex items-center gap-1.5"
              >
                <Play className="w-3.5 h-3.5 fill-white" />
                <span>Start Step Test</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
});
